/* eslint class-methods-use-this: 0 */
import {
  NO_ADDRESS,
  NO_SUPERCHARGERS,
  OUT_OF_RANGE,
  IMPOSSIBLE,
  SUPERCHARGER,
  DESTINATION_CHARGER,
  MAP,
  LIST,
  CLICK,
} from './Constants';

/**
 * AnalyticsUtils
 */
class AnalyticsUtils {
  /**
   * Constants
   */
  static get errors() {
    return {
      NO_ADDRESS,
      NO_SUPERCHARGERS,
      OUT_OF_RANGE,
      IMPOSSIBLE,
    };
  }

  static get chargerType() {
    return {
      SUPERCHARGER,
      DESTINATION_CHARGER,
    };
  }

  static get chargerSource() {
    return {
      MAP,
      LIST,
      CLICK,
    };
  }

  /**
   * Push a widget interaction to the dataLayer.
   *
   * @param {string} name - Name of the widget
   */
  static pushInteraction({ interaction, label }) {
    this.pushToDataLayer(this.getInteractionObject({ interaction, label }));
  }

  /**
   * Push route data to the dataLayer which is only received upon successful
   * response from routing API.
   *
   * @param {number} chargerStops - Number of supercharger stops
   */
  static pushRouteResponse(data) {
    const {
      tripStart,
      tripWaypoint,
      tripChargeStop,
      tripEnd,
      tripChargeDuration,
      tripTotalDurationMiles,
      tripTotalDurationTime,
      tripNumberChargeStops,
      tripNumberWaypointStops,
      tripCarModel,
    } = data;

    this.pushToDataLayer([
      this.getInteractionObject({
        interaction: 'details',
        tripStart,
        tripWaypoint,
        tripChargeStop,
        tripEnd,
        tripChargeDuration,
        tripTotalDurationMiles,
        tripTotalDurationTime,
        tripNumberChargeStops,
        tripNumberWaypointStops,
        tripCarModel,
      }),
    ]);
  }


  /**
   * Push route confirmation data to the dataLayer.
   *
   * @param {string} error - Error from server
   */
  static pushRouteError({ error }) {
    let errorLabel;

    // TODO: Identify if all these errors are possible.
    switch (error) {
      case NO_ADDRESS:
        errorLabel = 'cant_find_address';
        break;
      case NO_SUPERCHARGERS:
        errorLabel = 'no_superchargers_needed';
        break;
      case OUT_OF_RANGE:
        errorLabel = 'out_of_range';
        break;
      case IMPOSSIBLE:
        errorLabel = 'impossible_trip';
        break;
      default:
        errorLabel = null;
        break;
    }

    if (errorLabel) {
      this.pushToDataLayer(this.getInteractionObject({
        interaction: 'error',
        label: errorLabel,
      }));
    }
  }

  /**
   * Push the provided object to the dataLayer array.
   *
   * @param {object|array} data - GTM dataLayer object or array of objects
   * @return {array} - dataLayer array
   */
  static pushToDataLayer(data) {
    window.dataLayer = window.dataLayer || [];

    if (Array.isArray(data)) {
      window.dataLayer.push(...data);
    } else {
      window.dataLayer.push(data);
    }

    return window.dataLayer;
  }

  /**
   * Create the interaction object to be pushed to the data layer. The event
   * property is the same for all trip planner events, while the interaction
   * and any other additional properties are dependent upon the event.
   *
   * @param {string} interaction - Identifies the interaction
   * @param {object} params - Any other additional parameters to be pushed
   * @return {object} interaction - Object to be pushed to dataLayer
   */
  static getInteractionObject({ interaction, ...params }) {
    return Object.assign({ event: 'trip_planner', interaction }, params);
  }

  /**
   * Gets analytics address from the Google Maps geocoding API response.
   *
   * @param {object} parts - Address parts from Google Maps API response
   * @return {string} - Route location data stringified
   */
  static getAnalyticsFromGeocoding(parts) {
    const analyticsAddress = {
      planet: 'Earth',
    };

    if (parts && parts.length > 0) {
      parts.forEach((part) => {
        if (!part || !part.types) return;

        // Populate the city, state, zip code, and country properties.
        if (part.types.indexOf('locality') > -1) {
          analyticsAddress.city = part.long_name || part.short_name;
        } else if (part.types.indexOf('administrative_area_level_1') > -1) {
          analyticsAddress.state = part.long_name || part.short_name;
        } else if (part.types.indexOf('postal_code') > -1) {
          analyticsAddress.postal = part.long_name || part.short_name;
        } else if (part.types.indexOf('country') > -1) {
          analyticsAddress.country = part.long_name || part.short_name;
        }
      });
    }

    return analyticsAddress;
  }
}

export default AnalyticsUtils;
